import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { ARTICLES, getArticle, formatDateFr } from "../data/articles";

export default function Article() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = `${article.title} | MON DESSINATEUR BTP`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    if (metaDesc) metaDesc.setAttribute("content", article.excerpt);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, [article]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-heading text-3xl">Article introuvable</h1>
        <Link to="/articles" className="inline-block mt-6 underline">Retour aux articles</Link>
      </div>
    );
  }

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <article data-testid="article-page" className="bg-[#040914]">
      {/* Hero */}
      <header className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <Link to="/articles" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-10" data-testid="back-articles">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Retour aux articles
        </Link>
        <div className="flex items-center gap-4 text-xs text-white/50 mb-6">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> {formatDateFr(article.date)}
          </span>
          <span className="w-1 h-1 bg-white/30 rounded-full" />
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> {article.readTime} min de lecture
          </span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.05]">
          {article.title}
        </h1>
        <p className="mt-6 text-lg text-white/60 leading-relaxed">{article.excerpt}</p>
      </header>

      <div className="max-w-5xl mx-auto px-6">
        <div className="relative aspect-[16/7] overflow-hidden border border-[#1E2D4A]">
          <img src={article.cover} alt={article.title} className="w-full h-full object-cover opacity-85" />
        </div>
      </div>

      {/* Content */}
      <div
        data-testid="article-body"
        className="article-prose max-w-3xl mx-auto px-6 py-16"
        dangerouslySetInnerHTML={{ __html: article.content }}
      />

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-[#1E2D4A] mt-10">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <div className="text-xs tracking-[0.3em] uppercase text-white/40">À lire aussi</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/articles/${r.slug}`}
                  className="group border border-[#1E2D4A] bg-[#0A1326] p-6 hover:border-white/50 transition-colors"
                >
                  <div className="text-xs text-white/40">{formatDateFr(r.date)}</div>
                  <h3 className="font-heading text-xl mt-2 tracking-tight group-hover:underline underline-offset-4">{r.title}</h3>
                  <p className="text-sm text-white/60 mt-3 leading-relaxed">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-[#1E2D4A] bg-[#0A1326]">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h3 className="font-heading text-3xl sm:text-4xl tracking-tight">Un projet de plans à lancer ?</h3>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">
            Choisissez votre profil et obtenez un devis en ligne en 2 minutes. Intervention partout en France.
          </p>
          <Link
            to="/"
            className="inline-block mt-8 bg-white text-black px-8 py-3 text-sm tracking-wide hover:bg-white/85"
            data-testid="article-cta-home"
          >
            Voir les profils & services
          </Link>
        </div>
      </section>
    </article>
  );
}
